import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Lightbulb,
  Sparkles,
  Bug,
  Inbox,
  Eye,
  FileText,
  CheckCircle,
  ArrowRightCircle,
  XCircle,
  Archive,
  Loader2,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';

interface Request {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  type: 'idea' | 'feature' | 'bug';
  status: string;
  priority: string | null;
  createdAt: string;
  updatedAt: string;
}

const typeConfig = {
  idea: { icon: Lightbulb, label: 'Idea', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  feature: { icon: Sparkles, label: 'Feature', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  bug: { icon: Bug, label: 'Bug', color: 'text-red-500', bg: 'bg-red-500/10' },
};

const statusConfig: Record<string, { icon: typeof Inbox; label: string; color: string; bg: string }> = {
  new: { icon: Inbox, label: 'New', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  under_review: { icon: Eye, label: 'Under Review', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  plan_proposed: { icon: FileText, label: 'Plan Proposed', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  approved: { icon: CheckCircle, label: 'Approved', color: 'text-green-500', bg: 'bg-green-500/10' },
  converted: { icon: ArrowRightCircle, label: 'Converted', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  declined: { icon: XCircle, label: 'Declined', color: 'text-red-500', bg: 'bg-red-500/10' },
  archived: { icon: Archive, label: 'Archived', color: 'text-gray-500', bg: 'bg-gray-500/10' },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

export function RequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState<Request[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const currentStatus = searchParams.get('status') || '';
  const currentType = searchParams.get('type') || '';
  const currentSearch = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 20;

  useEffect(() => {
    async function fetchRequests() {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        if (currentStatus) params.append('status', currentStatus);
        if (currentType) params.append('type', currentType);
        if (currentSearch) params.append('search', currentSearch);
        params.append('page', currentPage.toString());
        params.append('limit', pageSize.toString());

        const response = await api.get<Request[]>(
          `/requests?${params.toString()}`
        ) as any;
        setRequests(response.data || []);
        setTotalCount(response.pagination?.total || 0);
        setError(null);
      } catch (err) {
        setError('Failed to load requests');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchRequests();
  }, [currentStatus, currentType, currentSearch, currentPage]);

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Group requests needing action
  const needsApproval = requests.filter(r => r.status === 'plan_proposed');
  const otherRequests = requests.filter(r => r.status !== 'plan_proposed');

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold">My Requests</h1>
          <p className="text-text-secondary mt-1">
            Track your ideas, features, and bug reports
          </p>
        </div>

        <Link
          to="/requests/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-dark text-white rounded-lg transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          New Request
        </Link>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-wrap items-center gap-3"
      >
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search requests..."
            value={currentSearch}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-text-muted" />
          <select
            value={currentStatus}
            onChange={(e) => updateFilter('status', e.target.value)}
            className="px-3 py-2 bg-bg-secondary border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
          >
            <option value="">All Statuses</option>
            {Object.entries(statusConfig).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>

          <select
            value={currentType}
            onChange={(e) => updateFilter('type', e.target.value)}
            className="px-3 py-2 bg-bg-secondary border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
          >
            <option value="">All Types</option>
            {Object.entries(typeConfig).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Content */}
      {!isLoading && !error && (
        <>
          {/* Needs Your Approval Section */}
          {needsApproval.length > 0 && !currentStatus && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-violet-500/10">
                  <FileText className="w-5 h-5 text-violet-500" />
                </div>
                <h2 className="text-lg font-semibold">Needs Your Approval</h2>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/10 text-violet-500">
                  {needsApproval.length}
                </span>
              </div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                {needsApproval.map((request) => (
                  <RequestRow key={request.id} request={request} highlight />
                ))}
              </motion.div>
            </motion.section>
          )}

          {/* Empty State */}
          {requests.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <Inbox className="w-16 h-16 mx-auto text-text-muted mb-4" />
              <h2 className="text-xl font-semibold mb-2">No requests yet</h2>
              <p className="text-text-secondary mb-6">
                Submit your first request to get started
              </p>
              <Link
                to="/requests/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-dark text-white rounded-lg transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                New Request
              </Link>
            </motion.div>
          )}

          {/* All Requests */}
          {(currentStatus || otherRequests.length > 0) && requests.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: needsApproval.length > 0 ? 0.25 : 0.15 }}
            >
              {!currentStatus && needsApproval.length > 0 && (
                <h2 className="text-lg font-semibold mb-4">All Requests</h2>
              )}

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                {(currentStatus ? requests : otherRequests).map((request) => (
                  <RequestRow key={request.id} request={request} />
                ))}
              </motion.div>
            </motion.section>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center gap-2 pt-4"
            >
              <button
                onClick={() => updateFilter('page', String(currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-bg-secondary border border-border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-text-secondary">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => updateFilter('page', String(currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-bg-secondary border border-border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}

function RequestRow({ request, highlight }: { request: Request; highlight?: boolean }) {
  const type = typeConfig[request.type];
  const status = statusConfig[request.status];
  const TypeIcon = type?.icon || Lightbulb;
  const StatusIcon = status?.icon || Inbox;

  return (
    <motion.div variants={itemVariants}>
      <Link to={`/requests/${request.id}`}>
        <div
          className={`p-4 bg-bg-secondary rounded-xl border transition-all hover:border-accent/30 ${
            highlight ? 'border-violet-500/30 shadow-lg shadow-violet-500/5' : 'border-border'
          }`}
        >
          <div className="flex items-center gap-4">
            {/* Type Icon */}
            <div className={`p-2 rounded-lg ${type?.bg || 'bg-gray-500/10'}`}>
              <TypeIcon className={`w-5 h-5 ${type?.color || 'text-gray-500'}`} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate">{request.title}</h3>
              <p className="text-sm text-text-secondary truncate">
                {request.projectName}
              </p>
            </div>

            {/* Status Badge */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status?.bg || 'bg-gray-500/10'} ${status?.color || 'text-gray-500'}`}>
              <StatusIcon className="w-3 h-3" />
              {status?.label || request.status}
            </div>

            {/* Date */}
            <span className="text-xs text-text-muted hidden sm:block">
              {formatDate(request.createdAt)}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
